import type { Breadcrumb, EventUser, Level } from './types';

/**
 * Ambient context attached to every event: user, tags, breadcrumbs.
 *
 * Breadcrumbs are the reason this exists — knowing what the user did in the
 * thirty seconds before a crash is usually more diagnostic than the stack.
 */
export class Scope {
  private user?: EventUser;
  private tags: Record<string, string> = {};
  private extra: Record<string, unknown> = {};
  private contexts: Record<string, unknown> = {};
  private breadcrumbs: Breadcrumb[] = [];

  constructor(private maxBreadcrumbs = 100) {}

  setUser(user: EventUser | undefined): this {
    this.user = user;
    return this;
  }

  /**
   * Set a tag, or remove it with `undefined` — for context that ends, such as
   * leaving a tenant's pages in a single-page app, where a stale value would
   * misattribute every later error.
   */
  setTag(key: string, value: string | undefined): this {
    if (value === undefined) delete this.tags[key];
    else this.tags[key] = value;
    return this;
  }

  setTags(tags: Record<string, string>): this {
    Object.assign(this.tags, tags);
    return this;
  }

  setExtra(key: string, value: unknown): this {
    this.extra[key] = value;
    return this;
  }

  setContext(key: string, value: unknown): this {
    this.contexts[key] = value;
    return this;
  }

  /**
   * Record a breadcrumb, evicting the oldest once the cap is reached.
   *
   * The cap is not optional: breadcrumbs accumulate for the life of a page, and
   * an unbounded list is a memory leak in someone else's application.
   */
  addBreadcrumb(breadcrumb: Breadcrumb): this {
    if (this.maxBreadcrumbs <= 0) return this;

    this.breadcrumbs.push({
      timestamp: breadcrumb.timestamp ?? new Date().toISOString(),
      level: breadcrumb.level ?? 'info',
      ...breadcrumb,
    });

    if (this.breadcrumbs.length > this.maxBreadcrumbs) {
      this.breadcrumbs.splice(0, this.breadcrumbs.length - this.maxBreadcrumbs);
    }
    return this;
  }

  clearBreadcrumbs(): this {
    this.breadcrumbs = [];
    return this;
  }

  /** Reset everything, e.g. on sign-out. */
  clear(): this {
    this.user = undefined;
    this.tags = {};
    this.extra = {};
    this.contexts = {};
    this.breadcrumbs = [];
    return this;
  }

  /** Snapshot for attaching to an event. Copies, so later mutation is safe. */
  snapshot(): {
    user?: EventUser;
    tags: Record<string, string>;
    extra: Record<string, unknown>;
    contexts: Record<string, unknown>;
    breadcrumbs: Breadcrumb[];
  } {
    return {
      user: this.user ? { ...this.user } : undefined,
      tags: { ...this.tags },
      extra: { ...this.extra },
      contexts: { ...this.contexts },
      breadcrumbs: [...this.breadcrumbs],
    };
  }
}

/** Map a console method to an event level. */
export function levelFromConsoleMethod(method: string): Level {
  switch (method) {
    case 'debug':
      return 'debug';
    case 'info':
    case 'log':
      return 'info';
    case 'warn':
      return 'warning';
    case 'error':
      return 'error';
    default:
      return 'info';
  }
}
